import { useState } from "react";
import { Link } from "react-router-dom";
import JsonLd from "./JsonLd";

export interface FaqItem {
    /** Customer-phrased question — these are extracted verbatim by AI engines. */
    question: string;
    /**
     * Plain-text answer. Keep concise (40–80 words) so AI overviews can quote
     * it cleanly. Markdown is NOT supported here on purpose — JSON-LD
     * `acceptedAnswer.text` should be plain text or HTML, and we keep it
     * plain so the rendered HTML and the schema match exactly.
     */
    answer: string;
    /** Optional in-page link rendered after the answer. Not part of the FAQPage text. */
    link?: { href: string; label: string };
}

interface FAQSectionProps {
    /** Stable id for the JSON-LD <script> tag (e.g. "faq-chat-widget"). */
    id: string;
    /** Section heading. Defaults to "Frequently Asked Questions". */
    heading?: string;
    /** Optional eyebrow above the heading (e.g. "Chat Widget · FAQ"). */
    eyebrow?: string;
    /** Optional supporting copy below the heading. */
    intro?: string;
    /** The Q/A list. Order matters — top items are most likely to be quoted. */
    items: FaqItem[];
}

/**
 * FAQSection — renders an accessible, expandable FAQ block AND injects a
 * matching `FAQPage` JSON-LD script so AI engines (ChatGPT, Perplexity,
 * Claude, Gemini) and Google's AI Overviews can surface these answers
 * directly. The on-page text and the schema text are identical because both
 * are sourced from the same `items` array.
 *
 * Place this near the bottom of every product / industry / pricing page.
 * The first 3–5 questions should match what a customer would actually type
 * into a search engine — schema docs: https://schema.org/FAQPage
 */
const FAQSection: React.FC<FAQSectionProps> = ({ id, heading = "Frequently Asked Questions", eyebrow, intro, items }) => {
    const [openIndex, setOpenIndex] = useState<number | null>(0);

    if (!items?.length) return null;

    const faqJsonLd = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: items.map((item) => ({
            "@type": "Question",
            name: item.question,
            acceptedAnswer: {
                "@type": "Answer",
                text: item.answer,
            },
        })),
    };

    return (
        <section id={`${id}-section`} aria-labelledby={`${id}-heading`} className="relative overflow-hidden">
            <div className="absolute inset-0 -z-10 opacity-30">
                <div className="pointer-events-none absolute -left-40 top-10 h-72 w-72 rounded-full bg-blue-500/20 blur-3xl" />
                <div className="pointer-events-none absolute -right-40 bottom-10 h-72 w-72 rounded-full bg-pink-500/15 blur-3xl" />
            </div>

            <div className="mx-auto max-w-4xl px-6 lg:px-8 py-20 lg:py-24">
                <div className="text-center mb-10">
                    {eyebrow && (
                        <div className="inline-flex items-center gap-2 rounded-full border border-slate-700/60 bg-slate-800/40 px-3 py-1 text-xs font-medium text-slate-300 mb-5">
                            <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-orange-500 to-pink-500" />
                            {eyebrow}
                        </div>
                    )}
                    <h2 id={`${id}-heading`} className="text-3xl sm:text-4xl font-bold tracking-tight text-white">
                        {heading}
                    </h2>
                    {intro && <p className="mt-4 max-w-2xl mx-auto text-slate-300 leading-relaxed">{intro}</p>}
                </div>

                <div className="space-y-3">
                    {items.map((item, idx) => {
                        const isOpen = openIndex === idx;
                        const buttonId = `${id}-q-${idx}`;
                        const panelId = `${id}-a-${idx}`;
                        return (
                            <article
                                key={item.question}
                                className="rounded-2xl border border-slate-800 bg-slate-900/70 overflow-hidden shadow-lg shadow-black/10"
                            >
                                <h3>
                                    <button
                                        id={buttonId}
                                        type="button"
                                        aria-expanded={isOpen}
                                        aria-controls={panelId}
                                        onClick={() => setOpenIndex(isOpen ? null : idx)}
                                        className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left text-white hover:bg-slate-800/60 transition-colors"
                                    >
                                        <span className="text-base sm:text-lg font-semibold">{item.question}</span>
                                        <svg
                                            className={`h-5 w-5 shrink-0 text-slate-400 transition-transform ${isOpen ? "rotate-180 text-orange-400" : ""}`}
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                            aria-hidden="true"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </button>
                                </h3>
                                {/* Answers stay in the HTML when collapsed so prerendered
                                    snapshots and non-JS crawlers see the same text as the
                                    FAQPage schema. `hidden` keeps them out of the a11y
                                    tree until the question is expanded. */}
                                <div id={panelId} role="region" aria-labelledby={buttonId} hidden={!isOpen}>
                                    <p className="px-6 pb-5 text-slate-300 leading-relaxed">{item.answer}</p>
                                    {item.link ? (
                                        <p className="px-6 pb-5 -mt-3">
                                            <Link to={item.link.href} className="font-medium text-orange-400 hover:text-orange-300">
                                                {item.link.label}
                                            </Link>
                                        </p>
                                    ) : null}
                                </div>
                            </article>
                        );
                    })}
                </div>
            </div>

            <JsonLd id={`faq-${id}`} data={faqJsonLd} />
        </section>
    );
};

export default FAQSection;
